import React, { useState } from 'react';
import { Button, Input, Box, LinearProgress, Alert, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import api from '../../api';

const CsvUploader = ({ onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [headers, setHeaders] = useState([]);
  const [questionColumn, setQuestionColumn] = useState('');
  const [answerColumn, setAnswerColumn] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile || null);
    setError('');
    setHeaders([]);
    setQuestionColumn('');
    setAnswerColumn('');

    if (selectedFile) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const text = evt.target.result;
          const firstLine = text.split(/\r\n|\n|\r/)[0];
          if (!firstLine) return;
          const parsed = firstLine.split(',').map((h) => h.trim().replace(/^["']|["']$/g, '')).filter(Boolean);
          if (parsed.length >= 2) {
            setHeaders(parsed);

            const qCandidates = ['user_question', 'question', 'user_input', 'query', 'prompt', 'input', 'text'];
            const detectedQ = parsed.find((h) => qCandidates.includes(h.toLowerCase()) && h.toLowerCase() !== 'question_html') || parsed[0];
            setQuestionColumn(detectedQ);

            const aCandidates = ['answer', 'output', 'response', 'completion', 'model_answer', 'bot_response', 'result'];
            const detectedA =
              parsed.find((h) => aCandidates.includes(h.toLowerCase()) && h !== detectedQ && h.toLowerCase() !== 'question_html') ||
              parsed.filter((h) => h !== detectedQ && h.toLowerCase() !== 'question_html')[0] ||
              parsed[1];
            setAnswerColumn(detectedA);
          }
        } catch (err) {
          console.error('Error reading CSV headers:', err);
        }
      };
      reader.readAsText(selectedFile.slice(0, 4096));
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    if (questionColumn) formData.append('question_column', questionColumn);
    if (answerColumn) formData.append('answer_column', answerColumn);

    try {
      setUploading(true);
      setError('');
      await api.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      onUploadSuccess();
      setFile(null); // Clear file input after success
      setHeaders([]);
      setQuestionColumn('');
      setAnswerColumn('');
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.response?.data?.message || 'File upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Input type="file" accept=".csv" onChange={handleFileChange} sx={{ mb: 2 }} />

      {headers.length > 2 && (
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f7fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" gutterBottom>
            Select columns to display as HTML:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', my: 1 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="question-col-label">Question / Prompt Column</InputLabel>
              <Select
                labelId="question-col-label"
                value={questionColumn}
                label="Question / Prompt Column"
                onChange={(e) => setQuestionColumn(e.target.value)}
              >
                {headers.map((h) => (
                  <MenuItem key={h} value={h}>
                    {h}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="answer-col-label">Answer Column</InputLabel>
              <Select
                labelId="answer-col-label"
                value={answerColumn}
                label="Answer Column"
                onChange={(e) => setAnswerColumn(e.target.value)}
              >
                {headers.filter((h) => h !== questionColumn).map((h) => (
                  <MenuItem key={h} value={h}>
                    {h}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Note: All other columns ({headers.filter((h) => h !== questionColumn && h !== answerColumn).join(', ')}) will be preserved as metadata and included in the exported results.
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 1 }}>
        <Button variant="contained" onClick={handleUpload} disabled={uploading || !file}>
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </Box>
      {uploading && <LinearProgress sx={{ mt: 2 }} />}
    </Box>
  );
};

export default CsvUploader;